import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { getPublicAvatarUrl } from '../../utils/publicAvatarUtils';
import { getAvatarFallbackText } from '../../utils/publicAvatarUtils';

// 지속적 캐시 (localStorage + 메모리)
const CACHE_KEY = 'optimized_avatar_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24시간

class PersistentImageCache {
  constructor() {
    this.memoryCache = new Map();
    this.loadFromStorage();
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        Object.entries(data).forEach(([key, value]) => {
          if (Date.now() - value.timestamp < CACHE_DURATION) {
            this.memoryCache.set(key, value.url);
          }
        });
      }
    } catch (error) {
      console.warn('Failed to load avatar cache from storage:', error);
    }
  }

  saveToStorage() {
    try {
      const data = {};
      this.memoryCache.forEach((url, key) => {
        data[key] = { url, timestamp: Date.now() };
      });
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save avatar cache to storage:', error);
    }
  }

  get(key) {
    return this.memoryCache.get(key);
  }

  set(key, url) {
    this.memoryCache.set(key, url);
    this.saveToStorage();
  }

  clear() {
    this.memoryCache.clear();
    localStorage.removeItem(CACHE_KEY);
  }
}

const imageCache = new PersistentImageCache();

/**
 * 최적화된 아바타 컴포넌트
 * - 지속적 캐싱 (localStorage + 메모리)
 * - 로딩 중 그라데이션 애니메이션
 * - 솔리드 배경 없이 투명한 로딩 상태
 */
const OptimizedAvatar = ({ 
  userID, 
  username, 
  size = 60, 
  fallbackUrl = null,
  style = {},
  onLoadChange = null,
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadAvatar = async () => {
      if (!userID) {
        setImageSrc(fallbackUrl);
        setLoading(false);
        return;
      }

      // 지속적 캐시에서 확인
      const imageSize = Math.ceil(size * 2); // 레티나 지원
      const cacheKey = `${userID}_${imageSize}`;
      const cachedUrl = imageCache.get(cacheKey);
      
      if (cachedUrl) {
        setImageSrc(cachedUrl);
        setLoading(false);
        setError(false);
        if (onLoadChange) {
          onLoadChange(true);
        }
        return;
      }

      setLoading(true);
      setError(false);

      try {
        const publicUrl = getPublicAvatarUrl(userID, imageSize);
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          imageCache.set(cacheKey, publicUrl);
          setImageSrc(publicUrl);
          setLoading(false);
          if (onLoadChange) {
            onLoadChange(true);
          }
        };
        
        img.onerror = () => {
          setError(true);
          setImageSrc(fallbackUrl);
          setLoading(false);
          if (onLoadChange) {
            onLoadChange(true);
          }
        };
        
        img.src = publicUrl;
        
      } catch (error) {
        console.error('Avatar loading failed:', error);
        setError(true);
        setImageSrc(fallbackUrl);
        setLoading(false);
      }
    };

    loadAvatar();
  }, [userID, size, fallbackUrl]);

  const fallbackText = getAvatarFallbackText(username);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.4,
        fontWeight: 600,
        color: '#7A8591',
        backgroundColor: 'transparent',
        // 테두리 원 그려지는 효과
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -2,
          left: -2,
          right: -2,
          bottom: -2,
          borderRadius: '50%',
          border: '2px solid #E4E7EA',
          opacity: !loading && style?.opacity === 1 ? 1 : 0,
          transform: !loading && style?.opacity === 1 ? 'scale(1)' : 'scale(0.8)',
          transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: !loading && style?.opacity === 1 ? 'borderDraw 0.8s ease-out forwards' : 'none',
        },
        '@keyframes borderDraw': {
          '0%': {
            clipPath: 'polygon(50% 0%, 50% 0%, 50% 50%, 50% 50%)',
            opacity: 0,
          },
          '25%': {
            clipPath: 'polygon(50% 0%, 100% 0%, 50% 50%, 50% 50%)',
            opacity: 0.3,
          },
          '50%': {
            clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 50% 50%)',
            opacity: 0.6,
          },
          '75%': {
            clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 0% 100%)',
            opacity: 0.8,
          },
          '100%': {
            clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%)',
            opacity: 1,
          },
        },
        ...style
      }}
      {...props}
    >
      {imageSrc && !loading && (
        <Box
          component="img"
          src={imageSrc}
          alt={username}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
          }}
        />
      )}
      {!imageSrc && !loading && fallbackText}
    </Box>
  );
};

export default OptimizedAvatar;